<template>
    <div>

        <!-- Search button -->
        <a class="search-btn" href="#" @click.prevent="openSearch">
            <IconBase
                iconName="searchIcon"
                :iconColor="consts.COLORS.LIGHT_800"
                display="flex"
                width="14"
                height="14"
                v-once
            />
        </a>

        <!-- Search field -->
        <transition name="search">
            <div class="search-field" v-if="open">
                <button class="back-btn" @click.prevent="closeSearch">
                    <IconBase
                        class="arrow-icon"
                        iconName="trendsArrowIcon"
                        :iconColor="consts.COLORS.PRIMARY_500"
                        width="12"
                        height="12"
                    />
                </button>
                <input
                    class="font-5"
                    type="text"
                    :value="searchText"
                    @input="handleInput"
                    ref="input"
                    placeholder="Search"
                />
                <button class="clear-btn"
                        v-show="searchText"
                        @click.prevent="clearSearch">
                    <IconBase
                        iconName="closeCircleIcon"
                        :iconColor="consts.COLORS.LIGHT_800"
                        display="flex"
                        width="13"
                        height="13"
                    />
                </button>
            </div>
        </transition>

    </div>
</template>

<script>
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import { debounce } from 'lodash';

const OPEN_ANIMATION_DURATION_MS = 500;

export default {
    name: 'AppListSearch',
    components: { IconBase },
    props: {
        open: {
            type: Boolean,
            default: false
        },
        searchText: {
            type: String,
            default: ''
        },
        debounceTime: {
            type: Number,
            default: 0
        }
    },
    computed: {
        setSearchTextDebounced() {
            return this.debounceTime
                ? debounce(this.setSearchText, this.debounceTime)
                : this.setSearchText;
        },
        consts: () => consts
    },
    methods: {
        setSearchText(text) {
            this.$emit('update:searchText', text);
        },
        clearSearchText() {
            this.setSearchText('');
        },
        openSearch() {
            this.$emit('update:open', true);
        },
        closeSearch() {
            this.clearSearchText();
            this.$emit('update:open', false);
        },
        clearSearch() {
            this.clearSearchText();
            this.focusInput();
        },
        focusInput() {
            const inputRef = this.$refs.input;
            if (inputRef) {
                inputRef.focus();
            }
        },
        handleInput(e) {
            const { value } = e.target;
            if (this.debounceTime) {
                this.setSearchTextDebounced(value);
            } else {
                this.setSearchText(value);
            }
        }
    },
    watch: {
        open(isOpen) {
            if (isOpen) {
                // if searched opened, focus input after animation completed
                setTimeout(() => this.focusInput(), OPEN_ANIMATION_DURATION_MS);
            } else {
                // if search closed, clear search text
                this.clearSearchText();
            }
        }
    }
};
</script>

<style scoped lang="scss">
@import "~styles/styleGuide.scss";

.search-field {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 5px 10px;

    button {
        border: none;
        position: absolute;
        outline: none;
        cursor: pointer;
    }

    .back-btn {
        left: 11px;
        border-radius: $borderRadius - 1px 0 0 $borderRadius - 1px;
        width: 32px;
        height: 23px;
        background-image: none;
        min-width: initial;
        padding: 0;
        .arrow-icon {
            transform: rotate(-90deg) translateY(1px);
        }
    }

    .clear-btn {
        background: transparent;
        right: 5px;
        height: 20px;
        min-width: 0;
    }
}

input {
    flex: 1;
    padding: 1px 30px 1px 35px;
    margin-left: 0;
    background-color: $white;
    outline: 0;
    right: 12px;
    border: $border;
    border-radius: $borderRadius;
    height: 25px;
    @include placeholder {
        color: $light-800;
    }

    // style during animation and when focused
    .search-enter-active &, &:focus {
        border-color: $primary;
    }
}

// Search Animation:
.search-enter, .search-leave-to {
    opacity: 0;
    transform: translateX(100%);
}

.search-enter-to, .search-leave {
    opacity: 1;
    transform: translateX(0);
}

.search-enter-active {
    // transition time should match OPEN_ANIMATION_DURATION_MS
    transition: all 0.5s ease-out;
}

.search-leave-active {
    transition: all 0.2s ease-out;
}
</style>
